import axios from 'axios'
import { format, utcToZonedTime } from 'date-fns-tz'

export class UnauthorizedException {}

let refreshInProgress = false
let requestsQueue = []

function onAccessTokenFetched(accessToken) {
  requestsQueue.forEach(callback => callback(accessToken))
  requestsQueue = [] // After retrying the failed requests, clear the queue.
}

function addRequestToQueue(callback) {
  requestsQueue.push(callback)
}

export function authBackend() {
  const token = window.localStorage.getItem('accessToken')

  if (!token) {
    throw new UnauthorizedException()
  }

  const params = {
    headers: { Authorization: 'Bearer ' + token },
    baseURL: process.env.VUE_APP_BACKEND_URL
  }

  const instance = axios.create(params)

  instance.interceptors.response.use(
    response => response, // If no error, return the response
    async error => {
      // If we receive a 401 error (token expired)
      if (error.response?.status === 401) {
        const refreshToken = window.localStorage.getItem('refreshToken')

        if (!refreshToken) {
          window.localStorage.removeItem('accessToken')
          window.location.href = '/login'
          return Promise.reject(error)
        }

        if (!refreshInProgress) {
          refreshInProgress = true

          try {
            const refreshResponse = await axios.post(`${process.env.VUE_APP_BACKEND_URL}/api/admin/refresh-token`, {
              refreshToken
            })

            const newAccessToken = refreshResponse.data.accessToken
            // Save new access token
            window.localStorage.setItem('accessToken', newAccessToken)

            onAccessTokenFetched(newAccessToken) // Notify that new token was fetched
            error.config.headers['Authorization'] = 'Bearer ' + newAccessToken // Retry original request

            return axios(error.config)
          } catch (refreshError) {
            window.localStorage.removeItem('accessToken')
            window.localStorage.removeItem('refreshToken')
            window.location.href = '/admin/login' // Redirect to login
          } finally {
            refreshInProgress = false
          }
        }

        // Queue this request until the token refresh is done
        return new Promise(resolve => {
          addRequestToQueue(newToken => {
            error.config.headers['Authorization'] = 'Bearer ' + newToken
            resolve(axios(error.config))
          })
        })
      }

      return Promise.reject(error)
    }
  )

  return instance
}

export function backend() {
  return axios.create({
    baseURL: process.env.VUE_APP_BACKEND_URL
  })
}

export function formatDate(dateStr) {
  return format(utcToZonedTime(dateStr, 'Europe/Tallinn'), 'yyyy-MM-dd HH:mm')
}
